import {
  cardAnatomy,
  inputAnatomy,
  menuAnatomy,
  popoverAnatomy,
  selectAnatomy,
  tabsAnatomy,
} from '@chakra-ui/anatomy';
import { createMultiStyleConfigHelpers, defineStyleConfig } from '@chakra-ui/react';
import { Alert, Modal } from './components.modal';
import { Kbd } from './components.kbd';
import { Button } from './components.button';

/**
 * Component style configurations for Bower
 * Customizes Chakra UI components to match the 2026-08 brand identity —
 * twilight blue on a warm parchment neutral ramp. See `theme/roles.ts`.
 */

// Shared semantic theme tokens used across component styles
const TEXT_PRIMARY = 'text.primary';
const BG_SURFACE = 'bg.surface';
const BORDER_DEFAULT = 'border.default';

// Button component styles

// Input component styles
const {
  definePartsStyle: defineInputPartsStyle,
  defineMultiStyleConfig: defineInputMultiStyleConfig,
} = createMultiStyleConfigHelpers(inputAnatomy.keys);

const Input = defineInputMultiStyleConfig({
  baseStyle: defineInputPartsStyle({
    field: {
      color: TEXT_PRIMARY,
      bg: BG_SURFACE,
      // Subtle but visible grey — aim for "I can see the field, not the
      // border". gray.200 reads as a soft chrome line on cream backgrounds
      // without competing with content. Previous alpha values were too
      // invisible on cream canvases.
      border: '1px solid',
      borderColor: 'border.field',
      _hover: {
        borderColor: 'border.field.hover',
      },
      _focus: {
        borderColor: 'border.focus',
        boxShadow: 'none',
      },
      _focusVisible: {
        borderColor: 'border.focus',
        boxShadow: 'none',
      },
      _invalid: {
        borderColor: 'border.error',
        boxShadow: 'none',
      },
      _placeholder: {
        color: 'text.muted',
      },
    },
  }),
  variants: {
    outline: defineInputPartsStyle({
      field: {
        bg: BG_SURFACE,
        border: '1px solid',
        borderColor: 'border.field',
        _hover: {
          borderColor: 'border.field.hover',
        },
        _focus: {
          borderColor: 'border.focus',
          boxShadow: 'none',
        },
        _focusVisible: {
          borderColor: 'border.focus',
          boxShadow: 'none',
        },
      },
    }),
    filled: defineInputPartsStyle({
      field: {
        bg: 'bg.subtle',
        border: '1px solid transparent',
        _hover: {
          bg: 'bg.hover',
        },
        _focus: {
          bg: BG_SURFACE,
          borderColor: 'border.focus',
        },
        _focusVisible: {
          bg: BG_SURFACE,
          borderColor: 'border.focus',
          boxShadow: 'none',
        },
      },
    }),
  },
  defaultProps: {
    variant: 'outline',
  },
});

// Textarea component styles
const Textarea = defineStyleConfig({
  baseStyle: {
    color: TEXT_PRIMARY,
    bg: BG_SURFACE,
    // NO `border` / `borderColor` here. `baseStyle` reaches EVERY variant,
    // including Chakra's own `flushed` (underline only) and `unstyled` (no
    // chrome), so a box declared here is drawn on controls that must not have
    // one — and the shorthand carries an implicit `border-color: currentcolor`,
    // which on `flushed` (whose `border-color` is `inherit`) turns the underline
    // into a full box in the ink colour. The `outline` variant owns the field
    // chrome and declares the shorthand BEFORE the colour, so the shorthand
    // cannot reset it. Pinned by __tests__/textareaVariantChrome.test.tsx.
    _hover: {
      borderColor: 'border.field.hover',
    },
    _focus: {
      borderColor: 'border.focus',
      boxShadow: 'none',
    },
    _focusVisible: {
      borderColor: 'border.focus',
      boxShadow: 'none',
    },
    _invalid: {
      borderColor: 'border.error',
      boxShadow: 'none',
    },
    _placeholder: {
      color: 'text.muted',
    },
  },
  variants: {
    outline: {
      bg: BG_SURFACE,
      border: '1px solid',
      borderColor: 'border.field',
      _hover: {
        borderColor: 'border.field.hover',
      },
      _focus: {
        borderColor: 'border.focus',
        boxShadow: 'none',
      },
      _focusVisible: {
        borderColor: 'border.focus',
        boxShadow: 'none',
      },
    },
    filled: {
      bg: 'bg.subtle',
      border: '1px solid transparent',
      _hover: {
        bg: 'bg.hover',
      },
      _focus: {
        bg: BG_SURFACE,
        borderColor: 'border.focus',
      },
      _focusVisible: {
        bg: BG_SURFACE,
        borderColor: 'border.focus',
        boxShadow: 'none',
      },
    },
  },
  defaultProps: {
    variant: 'outline',
  },
});

// Text component styles - default to text.primary for dark mode compatibility
const Text = defineStyleConfig({
  baseStyle: {
    color: TEXT_PRIMARY,
  },
});

// Heading — inherits theme.fonts.heading (GT Ultra Median) by default. Per-size
// tracking is applied via textStyles (h1/h2/h3) rather than a global baseStyle,
// so small Heading sizes don't get over-tightened. Variants opt in to editorial
// treatments: `display` → the heading face at its true weight for brand
// moments, `wordmark` → the Bower wordmark only.
//
// `display` sits at 400, not 700: GT Ultra Median is licensed in Regular only,
// and Chakra's own default is `fontWeight: bold`, so a Heading that asked for
// 700 got a browser-synthesised faux bold. That is not a subtle difference on a
// high-contrast face — it thickens the stems unevenly and reads as a font-
// loading fault. Caught on the dashboard hero ("Welcome back") by the rendered-
// weight audit; keep this at 400 unless a bold cut is licensed.
const Heading = defineStyleConfig({
  baseStyle: {
    color: TEXT_PRIMARY,
    // Chakra's Heading default is `bold`. Without this the whole component
    // faux-bolds by default, which is how the hero slipped through.
    fontWeight: 400,
  },
  variants: {
    display: {
      fontFamily: 'display',
      fontWeight: 400,
      letterSpacing: '-0.02em',
    },
    wordmark: {
      fontFamily: 'wordmark',
      fontWeight: 400,
      letterSpacing: 'normal',
    },
  },
});

// Link component styles — primary text color with persistent underline.
// Underline ensures links are distinguishable without relying on color alone (WCAG 1.4.1).
// Hover shifts to accent color for clear interactive feedback without layout shift.
const Link = defineStyleConfig({
  baseStyle: {
    color: TEXT_PRIMARY,
    // Must stay in step with the global `a:not(.chakra-button)` rule in
    // styles.ts — the two together are "the link treatment", and a link that
    // looks different depending on whether it happened to be a Chakra <Link>
    // is the drift this pair exists to prevent. globalLinkStyles.test.ts fails
    // if they disagree.
    fontWeight: 'medium',
    textDecoration: 'underline',
    // Must match the global anchor rule in styles.ts — see the note there on
    // why this is derived from currentColor rather than border.default.
    textDecorationColor: 'color-mix(in srgb, currentColor 50%, transparent)',
    textUnderlineOffset: '3px',
    textDecorationThickness: '1px',
    transition: 'color 0.15s, text-decoration-color 0.15s',
    _hover: {
      color: 'text.accent',
      textDecorationColor: 'currentColor',
    },
    _focusVisible: {
      outline: 'var(--chakra-borderWidths-interactive) solid',
      outlineColor: 'brand.primary',
      outlineOffset: '2px',
      borderRadius: 'sm',
    },
  },
});

// Divider component styles - use semantic border color
const Divider = defineStyleConfig({
  baseStyle: {
    borderColor: BORDER_DEFAULT,
  },
});

// Code component styles - semantic colors for code blocks
const Code = defineStyleConfig({
  baseStyle: {
    color: 'code.text',
    bg: 'code.bg',
    px: 1,
    py: 0.5,
    // `md`, matching the Bird chat chip, the two AttachmentDetail markdown
    // renderers and the TipTap editor's inline code. Five surfaces render an
    // inline code chip and they are one treatment, not five.
    borderRadius: 'md',
  },
});

/**
 * Badge — the one lozenge the whole app is built from.
 *
 * Shape and typography live in `baseStyle`, so every badge is the same
 * object; colour lives in ONE tone table (`badge.*` in semanticTokens.badge.ts)
 * that `badgeTone` interpolates into. Adding a tone is a row there plus a
 * name here, and restyling every badge in the product is an edit to either —
 * never a sweep of call sites.
 *
 * The 2026-09 lozenge pass changed four things. They are ONE decision — the
 * ring is what makes a badge read as an object, so nothing else has to — but
 * each undoes a choice that looked right on its own, so each is written down:
 *
 *   pill, not rounded-rect  `full` radius. `md` (6px) on a 20px-tall object
 *                           reads as a small button. Three call sites had
 *                           already overridden the radius by hand, two of
 *                           them to `full`.
 *   hairline                A fill alone dissolves into a tinted row, and a
 *                           fill loud enough not to is loud everywhere else.
 *                           The ring lets the fill stay quiet.
 *   sentence case           `textTransform: uppercase` + `letterSpacing:
 *                           wider` shouted every piece of metadata in the
 *                           product at the reader, and two call sites had
 *                           already opted out with `textTransform="none"`.
 *                           Labels render as written now, so a label that
 *                           should be loud is spelled loud at the call site.
 *   400, not 500            See the `fontWeight` note below.
 *
 * Colour comes from `variant`, NOT `colorScheme` — a Badge `colorScheme` is
 * inert here and always has been, because these variants name their tokens
 * explicitly instead of interpolating a `${colorScheme}.100` step. That trap
 * is pinned by __tests__/badgeVariantResolution.test.tsx.
 */
const badgeTone = (tone: 'neutral' | 'brand' | 'success' | 'warning' | 'error' | 'info') => ({
  bg: `badge.${tone}.bg`,
  color: `badge.${tone}.fg`,
  borderColor: `badge.${tone}.border`,
});

const Badge = defineStyleConfig({
  baseStyle: {
    // inline-flex + gap so a badge can carry an icon or a dot before its
    // label without the call site hand-rolling an HStack inside it.
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    // Keeps the pill on the text baseline when it sits inline in a sentence
    // rather than in a flex row — inline-flex alone drops it.
    verticalAlign: 'middle',
    borderRadius: 'full',
    borderWidth: '1px',
    borderStyle: 'solid',
    // Every variant sets its own; transparent is the floor so an unknown
    // variant renders a badge-shaped object rather than losing 2px of height.
    borderColor: 'transparent',
    // `normal` (400), not `medium`. With a ring around it a badge no longer
    // needs weight to read as an object, and 500 at 10-12px on a tint was the
    // last thing still making metadata shout. Every tone's ink/fill pair is
    // measured against the 4.5:1 NORMAL-text floor (the badge tone matrix in
    // __tests__/contrast.test.ts), so nothing here was leaning on weight.
    fontWeight: 'normal',
    letterSpacing: 'normal',
    textTransform: 'none',
    lineHeight: 'shorter',
    // A badge is usually not focusable, but two already are (`tabIndex={0}` on
    // the tooltip'd connector-status badges), and the global focus ring cannot
    // reach them on its own: Chakra's Badge baseStyle contributes
    // `boxShadow: var(--badge-shadow)`, which ties `*:focus-visible` on
    // specificity (0,1,0) and wins on order, because emotion inserts component
    // styles after the global ones. Restoring it here covers every variant,
    // including `outline`, which blanks box-shadow deliberately.
    _focusVisible: { boxShadow: 'outline' },
    flexShrink: 0, // never crush/clip a badge; the row wraps, not the badge
    whiteSpace: 'nowrap',
    // NO `maxW: '100%'`. It looks like the safe thing and is not: the label is
    // an anonymous flex item whose min-content width is the un-wrapped text, so
    // a cap on the border box does not shrink the text — it lets the text draw
    // OUTSIDE the fill and the ring. An un-capped badge that overflows its row
    // is the honest failure; a truncated pill with its label hanging out of it
    // is the confusing one. A call site needing a cap brings its own, plus
    // something that can truncate — see `DataTableTags`, which pairs `maxW`
    // with an inner truncating span.
  },
  /**
   * Three sizes, because call sites kept reaching for `fontSize` plus a
   * hand-tuned `px` and no two agreed on the padding. These are those
   * intents, named:
   *
   *   sm  dense chrome — breadcrumb "View only", a count on a button
   *   md  the default badge
   *   lg  table cells. Tag columns are scanned in bulk down a column rather
   *       than read one at a time, and `xs` at that job is genuinely hard to
   *       scan; `DataTableTags` had already made this call locally.
   */
  sizes: {
    sm: {
      fontSize: '2xs',
      px: 1.5,
      py: '1px',
    },
    md: {
      fontSize: 'xs',
      px: 2,
      py: 0.5,
    },
    lg: {
      fontSize: 'sm',
      px: 2.5,
      py: 0.5,
    },
  },
  variants: {
    // Semantic tones — the vocabulary call sites should be using.
    neutral: badgeTone('neutral'),
    brand: badgeTone('brand'),
    success: badgeTone('success'),
    warning: badgeTone('warning'),
    error: badgeTone('error'),
    info: badgeTone('info'),
    // Chakra's built-in names, kept so a stray `variant="subtle"` still
    // resolves to a real badge instead of an unstyled one.
    //
    // THESE THREE MERGE AS FUNCTIONS, the six tones above as plain objects.
    // Chakra defines `subtle`/`solid`/`outline` itself as colorScheme-aware
    // functions, so `extendTheme` returns `(props) => merge(chakraFn(props),
    // ourObject)` for the colliding names only. Ours wins, so the rendered
    // result is right — but anything READING the merged theme (a test, a
    // devtool) gets `undefined` for `.bg`/`.color` on exactly these three
    // unless it calls the entry first. It is written here rather than only in
    // the tests because this is where someone debugging a variant will look.
    subtle: badgeTone('brand'),
    solid: {
      bg: 'brand.primary',
      color: 'text.onBrand',
      borderColor: 'brand.primary',
    },
    outline: {
      bg: 'transparent',
      color: 'text.accent',
      borderColor: 'border.accent',
      // Chakra's own `outline` Badge draws its ring as an INSET BOX-SHADOW
      // (`--badge-shadow: inset 0 0 0 1px …`), and `extendTheme` merges that
      // in underneath this. Without suppressing it the variant renders two
      // concentric rings a pixel apart, in two different colours. Ours is a
      // real border so it participates in layout like every other variant's.
      boxShadow: 'none',
    },
  },
  defaultProps: {
    // `neutral`, not `subtle`. A bare `<Badge>` used to render the brand wash,
    // so the badges that cared least about attention were the ones painted in
    // the identity colour.
    variant: 'neutral',
    size: 'md',
  },
});

// FormLabel component styles
const FormLabel = defineStyleConfig({
  baseStyle: {
    color: TEXT_PRIMARY,
    fontWeight: 'medium',
    mb: 2,
  },
});

// Select component styles
const {
  definePartsStyle: defineSelectPartsStyle,
  defineMultiStyleConfig: defineSelectMultiStyleConfig,
} = createMultiStyleConfigHelpers(selectAnatomy.keys);

const Select = defineSelectMultiStyleConfig({
  baseStyle: defineSelectPartsStyle({
    field: {
      color: TEXT_PRIMARY,
      bg: BG_SURFACE,
      borderColor: BORDER_DEFAULT,
      _hover: {
        borderColor: 'border.hover',
      },
      _focus: {
        borderColor: 'border.focus',
        boxShadow: '0 0 0 1px var(--chakra-colors-border-focus)',
      },
      _focusVisible: {
        borderColor: 'border.focus',
        boxShadow: '0 0 0 1px var(--chakra-colors-border-focus)',
      },
      _invalid: {
        borderColor: 'border.error',
        boxShadow: '0 0 0 1px var(--chakra-colors-border-error)',
      },
      // Ensure dropdown options use semantic colors
      option: {
        color: TEXT_PRIMARY,
        bg: BG_SURFACE,
        _hover: {
          bg: 'bg.hover',
          color: TEXT_PRIMARY,
        },
        _selected: {
          bg: 'bg.active',
          color: TEXT_PRIMARY,
        },
        _focus: {
          bg: 'bg.hover',
          color: TEXT_PRIMARY,
        },
      },
    },
    icon: {
      color: 'text.muted',
    },
  }),
  variants: {
    outline: defineSelectPartsStyle({
      field: {
        bg: BG_SURFACE,
        border: '1px solid',
        borderColor: BORDER_DEFAULT,
        _hover: {
          borderColor: 'border.hover',
        },
        _focus: {
          borderColor: 'border.focus',
          boxShadow: '0 0 0 1px var(--chakra-colors-border-focus)',
        },
        _focusVisible: {
          borderColor: 'border.focus',
          boxShadow: '0 0 0 1px var(--chakra-colors-border-focus)',
        },
      },
    }),
    filled: defineSelectPartsStyle({
      field: {
        bg: 'bg.subtle',
        border: '1px solid transparent',
        _hover: {
          bg: 'bg.hover',
        },
        _focus: {
          bg: BG_SURFACE,
          borderColor: 'border.focus',
        },
        _focusVisible: {
          bg: BG_SURFACE,
          borderColor: 'border.focus',
          boxShadow: 'none',
        },
      },
    }),
  },
  defaultProps: {
    variant: 'outline',
  },
});

// Menu component styles
const {
  definePartsStyle: defineMenuPartsStyle,
  defineMultiStyleConfig: defineMenuMultiStyleConfig,
} = createMultiStyleConfigHelpers(menuAnatomy.keys);

const Menu = defineMenuMultiStyleConfig({
  baseStyle: defineMenuPartsStyle({
    list: {
      bg: 'bg.elevated', // floats above the canvas — white, not parchment
      color: TEXT_PRIMARY,
      minW: '3xs',
      p: '2', // Uniform padding for "even padding"
      borderRadius: 'xl',
      border: 'none',
      boxShadow: 'lg',
      // A menu MUST NOT be taller than the viewport. Two things go wrong when
      // it is, and the second one is not obvious:
      //   1. The overflowing items are unreachable — the list is portalled to
      //      <body>, which has no scrollbar (see below), so nothing scrolls.
      //   2. Chakra focuses the list on open with `preventScroll: false`
      //      (@chakra-ui/react menu/use-menu.mjs), so the browser scrolls the
      //      nearest scrollable ancestor to bring the list's top edge into
      //      view. The portal's containing block is <body> (`position:
      //      relative` in styles.ts), so it is BODY that scrolls — by roughly
      //      the list's top offset. The app shell is 100vh inside it, so the
      //      sticky 48px DetailTopBar is pushed above the viewport while the
      //      `position: fixed` nav rail stays put. On close Chakra restores
      //      focus with `preventScroll: true` and leaves the list mounted
      //      (`visibility: hidden`), so body stays tall and the scroll offset
      //      is never clamped back. `html, body { overflow: hidden }` means
      //      the user has no scrollbar and no wheel gesture to undo it: the
      //      header is simply gone until a reload. Reported 2026-08-23 as
      //      "open the kebab, close it, the whole header disappears", and only
      //      reproducible on a viewport short enough for the menu to overflow.
      // The cap does NOT close the class on its own, and it is worth being
      // precise about what it does buy. It bounds the list's HEIGHT, but the
      // failure needs its BOTTOM edge past the viewport — which also depends
      // on where the popper anchored it. So the scroll is guaranteed absent
      // only while the list is anchored within (viewport - cap) = 120px of the
      // top. Measured at 1280x620 with a 500px capped list:
      //
      //   anchor  56 (this kebab) → body.scrollTop   0, top bar at    0
      //   anchor 120 (the edge)   → body.scrollTop   1, top bar at   -1
      //   anchor 232              → body.scrollTop 113, top bar at -113
      //   anchor 400              → body.scrollTop 281, top bar at -281
      //
      // Popper does not rescue the lower anchors: Chakra passes
      // `preventOverflow: { boundary }` and Popper v2 defaults `altAxis:
      // false`, so for a top/bottom placement the main axis is x and there is
      // no vertical shift.
      //
      // The reported kebab lives in the 48px top bar at ~56, so the cap fixes
      // THAT outright, and it independently makes a long menu's bottom items
      // reachable on a short screen. For menus anchored further down —
      // sidebar tree rows, list-row kebabs — the document still scrolls and
      // `useDocumentScrollLock` is what corrects it. That hook is therefore
      // load-bearing for real menus today, not a spare for some future
      // surface.
      maxH: { base: 'calc(100dvh - 120px)', md: 'calc(100vh - 120px)' },
      // The width twin, never written until 2026-08-24: a MenuList sizes to its
      // CONTENT, so one long user-supplied row string pushes the row's trailing
      // controls off a phone screen. A component's own `maxW` REPLACES this
      // rather than composing with it, so any MenuList declaring one must fold
      // `100vw` in itself. Rationale, measurements and both guards live in
      // `theme/__tests__/menuListViewportCap.test.tsx`.
      maxW: 'calc(100vw - 24px)',
      overflowY: 'auto',
      // Don't chain a wheel gesture that hits the end of the list out to
      // whatever is behind the menu.
      overscrollBehavior: 'contain',
    },
    item: {
      color: TEXT_PRIMARY,
      bg: 'transparent',
      borderRadius: 'lg', // Rounded items like sidebar
      py: '2',
      px: '3',
      // mx removed, handled by list padding for even alignment
      mb: '1', // clear separation
      _hover: {
        bg: 'menu.item.hover',
        color: TEXT_PRIMARY,
        textDecoration: 'none',
        _disabled: {
          bg: 'transparent',
        },
      },
      _focus: {
        bg: 'menu.item.focus',
        color: TEXT_PRIMARY,
      },
      _active: {
        bg: 'menu.item.active',
        color: TEXT_PRIMARY,
      },
    },
    groupTitle: {
      color: 'text.muted',
      fontSize: 'xs',
      fontWeight: 'semibold',
      textTransform: 'uppercase',
      letterSpacing: 'wider',
      px: '4',
      py: '2',
    },
    command: {
      color: 'text.muted',
    },
    divider: {
      borderColor: 'border.muted',
      my: '2',
    },
  }),
});

// Popover component styles
const {
  definePartsStyle: definePopoverPartsStyle,
  defineMultiStyleConfig: definePopoverMultiStyleConfig,
} = createMultiStyleConfigHelpers(popoverAnatomy.keys);

const Popover = definePopoverMultiStyleConfig({
  baseStyle: definePopoverPartsStyle({
    content: {
      bg: BG_SURFACE,
      borderColor: BORDER_DEFAULT,
      color: TEXT_PRIMARY,
      borderRadius: 'xl', // More rounded per reference
      borderWidth: '1px',
      boxShadow: 'lg',
      _dark: {
        boxShadow: 'dark-lg',
      },
    },
    header: {
      color: TEXT_PRIMARY,
      borderColor: 'border.muted',
    },
    body: {
      color: TEXT_PRIMARY,
    },
    footer: {
      color: TEXT_PRIMARY,
      borderColor: 'border.muted',
    },
    closeButton: {
      color: TEXT_PRIMARY,
      _hover: {
        bg: 'bg.hover',
      },
    },
  }),
});

// Card component styles
const {
  definePartsStyle: defineCardPartsStyle,
  defineMultiStyleConfig: defineCardMultiStyleConfig,
} = createMultiStyleConfigHelpers(cardAnatomy.keys);

const Card = defineCardMultiStyleConfig({
  baseStyle: defineCardPartsStyle({
    container: {
      bg: BG_SURFACE,
      borderRadius: 'lg',
      boxShadow: 'md',
      border: '1px solid',
      borderColor: BORDER_DEFAULT,
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      _hover: {
        boxShadow: 'lg',
        transform: 'translateY(-2px)',
      },
    },
    header: {
      pb: 3,
    },
    body: {
      py: 4,
    },
    footer: {
      pt: 3,
    },
  }),
  variants: {
    // Project card variant (matching mockup design)
    project: defineCardPartsStyle({
      container: {
        bg: 'card.bg',
        border: '1px solid',
        borderColor: 'card.border',
        borderRadius: 'xl',
        p: 6,
        _hover: {
          borderColor: 'border.accent',
          boxShadow: 'xl',
          transform: 'translateY(-4px)',
        },
      },
    }),
  },
});

// Tooltip component styles
const Tooltip = defineStyleConfig({
  baseStyle: {
    bg: 'neutral.700',
    color: 'white',
    fontSize: 'sm',
    px: 3,
    py: 2,
    borderRadius: 'md',
    boxShadow: 'lg',
    maxW: '320px',
    '--popper-arrow-bg': 'var(--chakra-colors-gray-700)',
    _dark: {
      bg: 'neutral.600',
      color: 'white',
      '--popper-arrow-bg': 'var(--chakra-colors-gray-600)',
    },
  },
  // Note: Tooltip behavior props (openDelay, closeDelay, placement, etc.)
  // should be passed as component props, not theme config
  // They are runtime behavior options, not style properties
});

// Tabs component styles - ensure icons and text are properly colored in dark mode
const {
  definePartsStyle: defineTabsPartsStyle,
  defineMultiStyleConfig: defineTabsMultiStyleConfig,
} = createMultiStyleConfigHelpers(tabsAnatomy.keys);

const Tabs = defineTabsMultiStyleConfig({
  baseStyle: defineTabsPartsStyle({
    tab: {
      color: 'text.secondary',
      _selected: {
        color: TEXT_PRIMARY,
        fontWeight: 'semibold',
      },
      // Ensure all children (including icons) inherit proper colors
      '& *': {
        color: 'inherit',
      },
    },
    // No default tablist.borderColor — variants (pills, line, etc.) own
    // their own border styling. A `border.default` fallback here leaked
    // through the pills variant and rendered as a dark outline around the
    // Account Settings tabs container.
    tabpanel: {
      color: TEXT_PRIMARY,
    },
  }),
  variants: {
    // Modern pill-style tabs. No outer border — a subtle bg tint on the
    // container is enough to group the pills. Container shrinks to content
    // (width: 'fit-content') so it doesn't stretch across the page. Wraps
    // to multiple rows on narrow widths.
    // `pills` — the shared "segmented pill" visual language. Same shape and
    // colour values as SegmentedControl (used for Light/Dark/System + monthly/
    // annual pricing), so all tab-or-segment switchers across the app read as
    // one component family. If you change padding, colours, or radius here,
    // mirror the change in components/ui/SegmentedControl.tsx.
    pills: defineTabsPartsStyle({
      // root width must be fit-content, otherwise VStack `align=stretch`
      // parents push the tablist to full width and the pills spread apart.
      root: {
        width: 'fit-content',
        maxWidth: '100%',
      },
      tablist: {
        display: 'inline-flex',
        flexWrap: 'wrap',
        gap: 1,
        bg: 'bg.surface',
        border: '1px solid',
        // Subtler border — gray.200 read as "black outline" against cream.
        borderColor: 'border.muted',
        borderRadius: '2xl',
        p: 1,
        width: 'fit-content',
        maxWidth: '100%',
      },
      tab: {
        flex: 'none',
        fontSize: 'sm',
        fontWeight: 'medium',
        color: 'text.secondary',
        borderRadius: 'xl',
        px: 5,
        py: 2.5,
        border: 'none',
        transition: 'all 0.15s ease',
        _hover: {
          color: TEXT_PRIMARY,
          bg: 'bg.subtle',
        },
        _selected: {
          bg: 'brand.primary',
          color: 'text.onBrand',
          fontWeight: 'semibold',
          // Subtle lift on the selected pill so it pops against the pale
          // container — matches the handoff screenshot. Shares the `pill`
          // shadow token with SegmentedControl.
          boxShadow: 'pill',
          _hover: {
            bg: 'brand.hover', // semantic — was raw `brand.600` palette key (grayscale)
            color: 'text.onBrand',
          },
          _focusVisible: {
            // Selected pill keeps its lift + gets an outset brand ring for kb focus.
            boxShadow: 'var(--chakra-shadows-pill), 0 0 0 2px var(--chakra-colors-brand-primary)',
          },
        },
        _focusVisible: {
          boxShadow:
            'inset 0 0 0 var(--chakra-borderWidths-interactive) var(--chakra-colors-brand-primary)',
        },
        '& *': {
          color: 'inherit',
        },
      },
      tabpanel: {
        color: TEXT_PRIMARY,
        px: 0,
      },
    }),
  },
});

// Default colorScheme assignments for Chakra components.
//
// These five (+ Tabs below) are the only components that lean on Chakra's
// BUILT-IN colorScheme resolution — its own base styles reference raw
// `${colorScheme}.500` / `.600` palette steps directly (see e.g.
// @chakra-ui/theme's checkbox.mjs: `bg: mode(\`${c}.500\`, \`${c}.200\`)`).
// That mechanism needs a real registered Chakra palette with numeric steps.
//
// 'brand' is NOT one — it exists only as named semantic tokens
// (brand.primary, brand.surface, …), not a `50`-`900` ramp (see colors.ts's
// "one source per colour" note). So `colorScheme: 'brand'` silently failed to
// resolve here: the checked Checkbox/Radio background, the Switch track and
// the Progress bar all rendered with an unresolved `brand.500`, leaving (for
// Checkbox) a hardcoded-white tick with no background behind it — nearly
// invisible. `accent` is the one ramp that IS registered with real hex steps
// (colors.ts spreads `ROLES.accent`), and per semanticTokens.ts it currently
// shares the exact same shades as `brand` — so this is not a re-brand, just
// pointing at the ramp that already backs these tokens.
//
// Removing per-call colorScheme= props from app code is enforced by an
// ESLint rule (.eslintrc.js) so the theme stays the single source of truth.
const Progress = defineStyleConfig({
  defaultProps: { colorScheme: 'accent' },
});
const Checkbox = defineStyleConfig({
  defaultProps: { colorScheme: 'accent' },
});
const Switch = defineStyleConfig({
  defaultProps: { colorScheme: 'accent' },
});
const Radio = defineStyleConfig({
  defaultProps: { colorScheme: 'accent' },
});
// Tabs.pills overrides _selected.bg to brand.primary explicitly, so the
// colorScheme default only affects other Tabs variants (e.g. enclosed), which
// hit the same unresolved-'brand'-ramp issue described above.
Tabs.defaultProps = { ...Tabs.defaultProps, colorScheme: 'accent' };

export const components = {
  Button,
  Kbd,
  Input,
  Text,
  Heading,
  Link,
  Divider,
  Code,
  Badge,
  Textarea,
  FormLabel,
  Select,
  Menu,
  Popover,
  Card,
  Modal,
  Alert,
  Tooltip,
  Tabs,
  Progress,
  Checkbox,
  Switch,
  Radio,
};
